const StoredEntity = require('./stored-entity');
const RequestProcessStep = require('./processstep');
const FormField = require('./formfield');
const RequestFormField = require('./formfield');

function traverse(data, visitor, opts) {
	let depthFirst = opts && opts.depthFirst;
	function run(layer, stack, parent) {
		if (!depthFirst) visitor(layer, stack, parent);
		if (Array.isArray(layer)) {
			layer.forEach((item, i) => {
				let ns = stack.slice(0);
				ns.push(i);
				run(item, ns, layer);
			});
		} else if (typeof layer === 'object' && layer !== null) {
			Object.keys(layer).forEach(key => {
				let val = layer[key];
				let ns = stack.slice(0);
				ns.push(key);
				run(val, ns, layer);
			});
		}
		if (depthFirst) visitor(layer, stack, parent);
	}
	run(data, [], null);
}

class RequestProcess extends StoredEntity {

	constructor() {
		super();
		this.steps = [];	// RequestProcessStep loaded
		this.package = {}; //id, rev
		this._package = null; //loaded
		this.currentStep = 0;
		//anything needed for search/filtering/sorting
		this.status = 'open';	//open / accepted / rejected
		this.animalkind = {}; //id, rev
		this.offergroup = {};	//id, rev
		this.classification = {};	//id, rev
		this.country = {};	//id, rev
		this.members = [];	// { id, role, initiator?, editorName?, inProgress?, statusLabel?, noNotify? }
		this._members = null;	//loaded
		this.extraData = {};
		this.searchFilter = null;
		this.availableOffers = [];	// [{ id, rev }]
		this.followupRequest = null; // { id  rev }
		this.isFollowupFor = null;	//process-id
		this.hid = 0;
		this.agrAddress = {};
	}

	setHid(hid) {
		this.hid = hid;
	}

	getHid() {
		return this.hid;
	}

	getHidFormatted() {
		let b = `${this.hid}`;
		return `${b.substr(0, 3)}-${b.substr(3, 3)}-${b.substr(6, 4)}`;
	}

	setAgrAddress(a) {
		this.agrAddress = a;
	}

	getAgrAddress() {
		return this.agrAddress;
	}

	getMembers() {
		return this.members.map(e => e.id);
	}

	getMemberRole(id) {
		let mem = this.members.find(e => e.id === id);
		if (!mem) throw new Error(`no member found with id ${id}`);
		return mem.role;
	}

	getMembersWithRole() {
		return this.members;
	}

	getInitiator() {
		return this.members.find(e => e.initiator);
	}

	getLastClosedStep() {
		if (this.currentStep === 0) return null;
		let step = this.getStep(this.currentStep-1);
		return step;
	}

	initSteps(pkg) {
		this.setPackage(pkg);
		this.steps = pkg.getSteps().map(step => {
			let rps = new RequestProcessStep(this);
			let owners = this._getOwnersForStep(step);	//???
			rps.init(step, owners);
			return rps;
		});
		if (!this.steps.length) {
			throw new Error('no steps found');
		}
	}

	openFirstStep() {
		for (let step of this.steps) {
			if (step.forms.length === 0) {
				let idx = this.steps.findIndex(e => e === step);
				this.steps.splice(idx, 1);
			}
		}
		if (this.steps.length > 0) this.steps[0].setOpened();
		else this.status = 'closed';
	}

	setMembersLoaded(m) {
		let idx = new Map();
		m.forEach(mem => {
			idx.set(mem.id, mem);
		});
		this._members = Array.from(idx.values());
	}

	getMembersLoaded() {
		if (!this._members) throw new Error('members are not loaded');
		return this._members;
	}

	updateMembers(m) {
		this.members = m;
		this.getSteps().forEach(step => {
			if (step.closedDate || step.openedDate) return;
			let owners = this._getOwnersForStep(step.getStepTemplate());
			step.initForms(owners);
		});
	}

	getMemberRoleIndex() {
		return Object.fromEntries(this.members.map(e => {
			return [e.id, e.role];
		}));
	}

	_getOwnersForStep(step) {
		let index = this.getMemberRoleIndex();
		return this.getMembersLoaded().filter(m => index[m.id] === step.getRole()).map(m => m.id);
	}

	initEnv(env, offergroup, animalkind, pkg, classification, country, members, previousRequest, type) {
		this.package = {
			id: pkg.getId(),
			rev: pkg.getRev()
		};
		this.setPackage(pkg);
		this.animalkind = { id: animalkind.id, rev: animalkind.rev };
		this.offergroup = { id: offergroup.id, rev: offergroup.rev };
		this.classification = { id: classification.id, rev: classification.rev };
		this.country = { id: country.id, rev: country.rev };
		this.members = members;
		this.status = 'open';
		this.type = type;
		if (previousRequest) {
			this.isFollowupFor = previousRequest.getId();
		}
	}

	getType() {
		return this.type;
	}

	setPackage(pkg) {
		this._package = pkg;
	}

	getPackage() {
		return this._package;
	}

	setSteps(steps) {
		this.steps = steps;
	}

	getSteps() {
		return this.steps;
	}

	getStep(n) {
		if (!this.steps[n]) throw new Error(`out of bounds step: ${n}`);
		return this.steps[n];
	}

	getStepFieldsToLoad() {
		return this._steps.flatMap(step => step.dynFields);
	}

	setFormData(owner, data, attachments, actingUserId) {
		let role = this.getMemberRole(owner);
		let step = this.getStepByRole(role);
		let form = step.getFormByOwner(owner);
		form.fillData(data, attachments, actingUserId);
		if (step.canClose()) {
			this._advanceStep();
		}
	}

	getMembersNotifiedBetweenSteps(from, to) {
		let res = new Map();
		for (let i=from; i<to; i++) {
			let step = this.steps[i];
			if (!step) continue;
			let notify = this.getMembersNotifiedOnStep(step);
			for (let member of notify) {
				res.set(member.id, member);
			}
		}
		return Array.from(res.values());
	}

	getMembersNotifiedOnStep(step) {
		let tpl = step.getStepTemplate();
		let roles = tpl.getNotifiedRolesOnClose();
		return this.getMembersWithRole()
			.filter(({ role, noNotify }) => !noNotify && roles.includes(role))
			.map(({ id }) => this.getMembersLoaded().find(e => e.id === id));
	}

	autoAdvanceStep(force) {
		let step = this.getCurrentStep();
		if (!force && !step.hasAutoResponseEnabled()) throw new Error('step has no auto-response enabled');
		step.setAutoClosed();
		this._advanceStep();
		return this.getMembersNotifiedOnStep(step);
	}

	_advanceStep() {
		let step = this.getCurrentStep();
		if (!step) return;
		// console.log('advance step', this.currentStep, step);
		if (step.isOpen()) {
			if (!step.canClose()) return;
			step.setClosed();
		}
		this.currentStep += 1;
		if (this.currentStep === this.steps.length) {
			this.calculateStatus();
			return;
		}
		let nextStep = this.getCurrentStep();
		if (!nextStep.canOpen()) {
			this._advanceStep();
			return;
		}
		nextStep.setOpened();
	}

	calculateStatus() {
		let lastStep = this.steps[this.steps.length-1];
		if (lastStep.closedDate) {
			let res = this.getDataByTag(FormField.MAIN_ANSWER);
			if (res.length) {
				let [fieldId, lastValue] = res.pop();	//assumes that it is a true/false radio
				let field = this.getPackage().getFieldById(fieldId);
				let option = field.getOptions().find(o => o.id === lastValue);
				if (option && ['accepted', 'rejected'].includes(option.key)) {
					this.status = option.key;
				} else {
					this.status = 'closed';
				}
			} else {
				this.status = 'closed';
			}
		} else {
			this.status = 'open';
		}

	}

	getStatus() {
		return this.status;
	}

	canSetFormData(owner) {
		let role = this.getMemberRole(owner);
		let step = this.getStepByRole(role);
		if (!step.openedDate || step.closedDate) throw new Error('step is not open');
		let form = step.getFormByOwner(owner);
		if (!form) throw new Error('no owned form in this step');
	}

	toFacts() {
		const res = {};
		res.steps = this.getSteps().reduce((c, v) => {
			c[v.getRole()] = {
				active: true,
				done: !!v.closedDate,
				fieldData: null,
				extraData: v.extraData
			};
			if (v.getForms().length === 1) {
				let form = v.getForms()[0];
				// c[v.getRole()].fieldData = form.fieldData;
				c[v.getRole()].tagData = Object.fromEntries(RequestFormField.TAGS.map(t => {
					let values = form.getDataByTag(t, true);
					if (!values.length) return [];
					return [t, values[0][1]];
				}));
			}
			return c;
		}, {});
		let aso = this.members.find(e => e.role === 'aso');
		let ins = this.members.find(e => e.role === 'ins');
		let aut = this.members.find(e => e.role === 'aut');
		res.env = {
			animalkind: this.animalkind.id,
			pkg: this.package.id,
			offergroup: this.offergroup.id,
			country: this.country.id,
			userAso: aso ? aso.id : null,
			userIns: ins ? ins.id : null,
			userAut: aut ? aut.id : null,
			type: this.getType()
		};
		res.extra = this.extraData;
		res.followupRequest = this.followupRequest || {};
		res.searchfilter = this.filterToFacts();
		return res;
	}

	filterToFacts() {
		const res = {};
		if (!this.searchFilter) return res;
		traverse(this.searchFilter, (layer, stack, parent) => {
			if (!layer.comparison || stack.includes('$not')) return;
			let field, value;
			if (layer.field === '/data/dynamicFields/field') {
				field = layer.value;
				value = parent[1].value;
			} else {
				if (layer.field.includes('dynamicFields')) return;
				field = layer.field.split('/').pop();
				value = layer.value;
			}
			res[field] = { value };
		});
		return res;
	}

	fromFacts(facts) {
		if (facts.steps) {
			Object.entries(facts.steps).forEach(([role, stepdata]) => {
				if (stepdata.removeMembers) {
					this.members = this.members.filter(e => e.role !== role || e.initiator);
				}
				let step;
				try {
					step = this.getStepByRole(role);
				} catch (e) {
					return;
				}
				if (!step) return;
				if (stepdata.active === false) {
					let idx = this.steps.findIndex(e => e === step);
					this.steps.splice(idx, 1);
				} else if (stepdata.done === true) {
					if (this.getCurrentStep() === step) {
						this.autoAdvanceStep(true);
					} else if (!step.closedDate) {
						step.setClosed(true);
					}
				}
			});
		}
		if (facts.extra) {
			this.extraData = facts.extra;
		}
		if (facts.notification) {
			if (facts.notification.surpress) {
				Object.entries(facts.notification.surpress).forEach(([role, supr]) => {
					this.members.filter(m => m.role === role).forEach(m => {
						m.noNotify = supr;
					});
				});
			}
			if (facts.notification.filter) {
				Object.entries(facts.notification.filter).forEach(([role, filter]) => {
					this.members
						.filter(m => m.role === role)
						.map(({ id }) => this.getMembersLoaded().find(e => e.id === id))
						.forEach(m => {
							m.emailFilter = filter;
						});
				});
			}
		}
		this.followupRequest = facts.followupRequest && facts.followupRequest.id ? facts.followupRequest : null;
		if (facts.field) {
			this.getSteps().forEach(step => {
				step.setFields(step.getStepTemplate().getFields().filter(f => !(facts.field[f.id] && facts.field[f.id].hide)));
			});
		}
		if (this.getCurrentStep() && this.getCurrentStep().closedDate) {
			this._advanceStep();
		} else if (this.currentStep >= this.steps.length) {
			this.currentStep = this.steps.length;
			this.calculateStatus();
		}
	}

	retract() {
		this.status = 'retracted';
		for (let step of this.steps) {
			if (step.openedDate && !step.closedDate) {
				step.openedDate = null;
			}
		}
	}

	canBeRetracted() {
		const initiatorId = this.getInitiator().id;
		return this.getSteps().filter(s => {
			if (!s.closedDate) return false;
			if (s.autoResponse && s.autoResponse.closed) return false;
			let notByInitiator = s.getForms().filter(f => f.filledDate && f.owner !== initiatorId);
			if (notByInitiator.length > 0) {
				return true;
			}
			return false;
		}).length === 0;
	}

	getCurrentStep() {
		return this.steps[this.currentStep];
	}

	getStepByRole(r) {
		let step = this.steps.find(s => s.role === r);
		if (!step) throw new Error(`no step for role: ${r}`);
		return step;
	}

	getDataByTag(tag) {
		return this.steps.flatMap(s => s.getDataByTag(tag));
	}

	setSearchFilterAndResult(filter, result) {
		this.searchFilter = filter ? filter : null;
		this.availableOffers = result.map(o => {
			return {
				id: o.id,
				rev: o.rev
			};
		});
	}

	getSearchFilter() {
		return this.searchFilter;
	}

	getAvailableOffers() {
		return this.availableOffers;
	}

	getFollowupRequest() {
		return this.followupRequest && this.followupRequest.id
			? this.followupRequest : null;
	}

	getFollowupFor() {
		return this.isFollowupFor;
	}

	setMemberStatus(memberId, data) {
		let member = this.members.find(e => e.id === memberId);
		if (!member) throw new Error(`member not found: ${memberId}`);
		Object.assign(member, data);
		return member;
	}

	fromData(data) {
		Object.keys(data).forEach(key => {
			let val = data[key];
			if (key === 'steps') {
				val = val.map(v => {
					let step = new RequestProcessStep(this);
					step.fromData(v);
					return step;
				});
				this.setSteps(val);
			} else {
				this[key] = val;
			}
		});
		if (typeof data.extraData === 'string') this.extraData = JSON.parse(data.extraData);
		if (typeof data.searchFilter === 'string') this.searchFilter = JSON.parse(data.searchFilter);
	}

	toStorage() {
		let data = super.toStorage();
		data.steps = data.steps.map(s => s.toStorage());
		data.extraData = JSON.stringify(data.extraData);
		data.searchFilter = JSON.stringify(data.searchFilter);
		return data;
	}

	fromStorage(entity) {
		// console.log('process fromstorage', entity);
		super.fromStorage(entity);
		this.steps = entity.data.steps.map(sd => {
			let step = new RequestProcessStep(this);
			step.fromData(sd);
			return step;
		});
	}

	toExternal() {
		let res = super.toExternal();
		res.steps = res.steps.map(step => {
			step.forms = step.forms.map(form => {
				form.fieldData = JSON.parse(form.fieldData);
				return form;
			});
			step.extraData = JSON.parse(step.extraData);
			return step;
		});
		res.searchFilter = this.searchFilter;
		res.extraData = this.extraData;
		return res;
	}

	toJSON() {
		return this.toExternal();
	}

}


module.exports = RequestProcess;
